import { dedupeList, type Nullable, type OptionalString } from '@amzn/elevate-data-types';

import { amplifyFetch } from '@/common/data-provider';
import { ElevateDateTime } from '@/common/date-time-helpers';
import { cleanPermissionGroup, sort } from '@/common/utility';
import type { BarRaiserDefinition, OrgBarRaiserDefinition } from '@/components/BarRaiser/models';
import {
  getAllOrgCollaborations,
  getOrgRoleGroups as getOrgRoleGroupsQuery,
  getOrgRoleLDAPGroups,
  getProfilesByOrgAndRole,
  listOrganizationsSimple,
  listOrgTrainings,
  listprofilesByBRStatus,
} from '@/graphql/queries';
import {
  BarRaiserStatus,
  gql,
  type ModelOrgRoleLdapGroupsFilterInput,
  type ModelRolePermissionGroupFilterInput,
  type ModelTrainingFilterInput,
  OrganizationStatus,
  ProfileStatus,
  Roles,
} from '@/graphql/types';
import { CollaborationItem, OrgTrainingItem } from '@/models';
import featureFlags from '@/utilities/feature-flags';

async function getRoleGroupsClassic(
  orgId: string,
  roles?: Nullable<Roles | Roles[]>
): Promise<{ group: string; role: Roles }[]> {
  let filter: Nullable<ModelOrgRoleLdapGroupsFilterInput>;
  if (roles) filter = { or: (Array.isArray(roles) ? roles : [roles]).map((v) => ({ role: { eq: v } })) };
  if (filter?.or?.length === 1) filter = filter.or[0];
  const document = gql(getOrgRoleLDAPGroups);
  const data = await amplifyFetch({ document, variables: { id: orgId, roleLDAPGroupsFilter: filter || null } });
  return data.getOrganization.roleLDAPGroups?.items ?? [];
}

async function getRoleGroupsPlatform(
  orgId: string,
  roles?: Nullable<Roles | Roles[]>
): Promise<{ group: string; role: Roles }[]> {
  let filter: Nullable<ModelRolePermissionGroupFilterInput>;
  if (roles) filter = { or: (Array.isArray(roles) ? roles : [roles]).map((v) => ({ role: { eq: v } })) };
  if (filter?.or?.length === 1) filter = filter.or[0];
  const document = gql(getOrgRoleGroupsQuery);
  const data = await amplifyFetch({ document, variables: { id: orgId, roleGroupsFilter: filter || null } });
  return (data.getOrganization.roleGroups?.items ?? []).map((i) => ({ ...i, group: i.teamId }));
}

export async function getOrgRoleGroups(
  orgId: OptionalString,
  roles?: Nullable<Roles | Roles[]>
): Promise<Map<Roles, string>> {
  if (!orgId) return new Map();
  const fn = featureFlags.PERMISSION_GROUP_MODEL.value === 'classic' ? getRoleGroupsClassic : getRoleGroupsPlatform;
  const items = await fn(orgId, roles);
  return new Map(items.map((item) => [item.role, cleanPermissionGroup(item.group)]));
}

export async function getOrgAdminGroup(orgId: OptionalString): Promise<OptionalString> {
  let orgRoleToGroup = new Map<Roles, string>();
  try {
    orgRoleToGroup = await getOrgRoleGroups(orgId, Roles.ORG_ADMIN);
  } catch (ex) {
    console.warn('Hit error retrieving org admin LDAP group name', ex);
  }
  return orgRoleToGroup.values().next().value as OptionalString;
}

export async function getOrgIdNameMapping(): Promise<Map<string, string>> {
  const data = await amplifyFetch({
    document: gql(listOrganizationsSimple),
    variables: { filter: { status: { eq: OrganizationStatus.ACTIVE } } },
  });
  return new Map((data.listOrganizations.items ?? []).map((item) => [item.id, item.name]));
}

export async function getCollaborationDefinitions(orgId: OptionalString): Promise<CollaborationItem[]> {
  if (!orgId) return [];
  const document = gql(getAllOrgCollaborations);
  const data = await amplifyFetch({ document, variables: { id: orgId } });
  const inbound = data.getOrganization.orgCollaborationsInbound?.items ?? [];
  const outbound = data.getOrganization.orgCollaborationsOutbound?.items ?? [];
  const all = [...inbound, ...outbound];
  return all.map((item) => ({
    ...item,
    barRaisers: item.barRaisers ?? false,
    originatingOrgID: 'originatingOrgID' in item ? item.originatingOrgID : null,
    targetOrgID: 'targetOrgID' in item ? item.targetOrgID : null,
    originParticipants: item.originParticipants?.items ?? [],
    targetParticipants: item.targetParticipants?.items ?? [],
  }));
}

export async function getPreferredBarRaisers(orgId: OptionalString): Promise<OrgBarRaiserDefinition[]> {
  if (!orgId) return [];
  const document = gql(getProfilesByOrgAndRole);
  const results = await amplifyFetch({
    document,
    variables: { orgID: orgId, filter: { role: { contains: Roles.PREFERRED_BR } } },
    onFetchNextToken: (v) => v.listProfilesByOrgV2.nextToken || null,
  });
  const barRaisers: OrgBarRaiserDefinition[] = results.flatMap((result) =>
    (result.listProfilesByOrgV2?.items ?? [])
      .filter((item) => !!item.profile)
      .map(
        (item): OrgBarRaiserDefinition => ({
          id: item.profile!.id,
          alias: item.profile!.alias,
          email: item.profile?.email,
          name: item.profile?.name ?? '',
          level: item.profile?.level,
          isPreferred: true,
          levelFilter: item.profile?.level ? `L${item.profile.level}` : undefined,
          orgId: item.orgID,
          orgName: '',
          recordId: `${item.profileID}-${item.orgID}`,
          status: item.profile?.status ? ProfileStatus[item.profile.status] : ProfileStatus.ACTIVE,
          title: item.profile?.title,
          updatedAt: item.updatedAt,
        })
      )
  );
  return dedupeList(barRaisers, 'alias');
}

export async function getBRStatusProfiles(brStatus: BarRaiserStatus, orgIds?: string[]): Promise<BarRaiserDefinition[]> {
  const document = gql(listprofilesByBRStatus);
  const results = await amplifyFetch({
    document,
    variables: { brStatus, brOrgFilter: orgIds ? { or: orgIds.map((id) => ({ orgID: { eq: id } })) } : null },
    onFetchNextToken: (v) => v.listProfilesByBrStatus.nextToken || null,
  });
  const barRaisers: BarRaiserDefinition[] = results.flatMap((result) =>
    (result.listProfilesByBrStatus.items ?? [])
      .filter((item) => !!item)
      .map(
        (item): BarRaiserDefinition => ({
          id: item.id,
          alias: item.alias,
          email: item.email,
          name: item.name ?? '',
          level: item.level,
          isPreferred: !!item.orgs?.items.length,
          levelFilter: item.level ? `L${item.level}` : undefined,
          recordId: item.id,
          status: item.status ? ProfileStatus[item.status] : ProfileStatus.ACTIVE,
          title: item.title,
          updatedAt: item.updatedAt,
        })
      )
  );
  return dedupeList(barRaisers, 'alias');
}

export async function getOrgTrainings(
  orgId: OptionalString,
  timezoneId: OptionalString,
  activeOnly?: boolean,
  filter?: ModelTrainingFilterInput
): Promise<OrgTrainingItem[]> {
  if (!orgId) return [];
  const isActiveFilter = { isActive: { eq: true } };
  let trainingFilter: ModelTrainingFilterInput | undefined = filter;
  if (activeOnly) trainingFilter = trainingFilter ? { and: [trainingFilter, isActiveFilter] } : isActiveFilter;
  const result = await amplifyFetch({
    document: gql(listOrgTrainings),
    variables: { id: orgId, trainingFilter },
  });
  return (result.getOrganization.trainings?.items ?? [])
    .map(
      (item): OrgTrainingItem => ({
        ...item,
        filterMinVersion: Number.parseFloat(item.minAcceptedVersion ?? '0'),
        filterTrainingVersion: Number.parseFloat(item.trainingVersion ?? '0'),
        isActive: item.isActive ?? false,
        isManaged: item.isManaged ?? false,
        status: item.isActive ? 'Active' : 'Inactive',
        created: ElevateDateTime.fromUTC(item.createdAt, timezoneId),
        updated: ElevateDateTime.fromUTC(item.updatedAt, timezoneId),
      })
    )
    .sort((a, b) => sort(a.title, b.title));
}
